// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { SUPPORT_REFERENCE_FILTER, normalizeSupportReference, } from "@/resources/Approvals/supportReference";
import { buildQuery, buildVariables } from "ra-data-hasura";
import { getElectoralLogVariables, getElectoralLog, electoralLogInstantFilters, ELECTORAL_LOG_RANGE_FILTERS, ELECTORAL_LOG_ZONE_FILTER, ELECTORAL_LOG_DEFAULT_ZONE_FILTER, LIST_TENANT_LOG, TENANT_LOG_CONTEXT, } from "./ListElectoralLog";
import { ELogScope, LOG_FILTER_EVENT_TYPE, LOG_FILTER_LOG_TYPE, LOG_SCOPE_META, } from "@/components/logs/logScope";
import { LIST_USERS, customBuildGetUsersVariables } from "./GetUsers";
import { getPermissions } from "./GetPermissions";
import { getRoles } from "./GetRoles";
import { isString } from "lodash";
import { COLUMNS_MAP } from "@/types/query";
import { FORENSIC_NETWORK_SUMMARY } from "./ForensicNetwork";
import { GET_ELECTIONS_BY_EXTERNAL_ID } from "./GetElectionsByExternalID";
import { LOG_FILTER_CHANNEL, LOG_FILTER_OUTCOME } from "@/components/logs/messagingLogFilters";
var UUID_REGEXP = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * `labels`/`annotations` are free-form jsonb columns. ra-data-hasura's default
 * jsonb filter tries to JSON.parse the typed text and use `_contains`, which
 * silently no-ops for plain text (see typeAwareKeyValueReducer in
 * ra-data-hasura). Route these through a raw query that casts the column to
 * text and does a substring search instead.
 */
function applyJsonbTextSearchFilter(filter, field) {
    var value = filter[field];
    if (typeof value === "string" && value.length > 0) {
        filter[field] = {
            format: "hasura-raw-query",
            value: { _cast: { String: { _ilike: "%".concat(value, "%") } } },
        };
    }
}
/**
 * `uuid` columns reject non-uuid input at the Postgres level, so a partial
 * value typed by the user (before finishing pasting a full id) would error
 * out the whole list query. Only forward the filter once it's a full uuid.
 */
function applyUuidFilter(filter, field) {
    var value = filter[field];
    if (typeof value !== "string" || value.length === 0) {
        return;
    }
    if (UUID_REGEXP.test(value)) {
        filter[field] = { format: "hasura-raw-query", value: { _eq: value } };
    }
    else {
        delete filter[field];
    }
}
var BOOLEAN_OPERATORS = ["_and", "_or", "_not"];
var isRawQuery = function (value) {
    return typeof value === "object" &&
        value !== null &&
        value.format === "hasura-raw-query";
};
var GRAPHQL_INT_MIN = -2147483648;
var GRAPHQL_INT_MAX = 2147483647;
var INTEGER_REGEXP = /^-?\d+$/;
var isIntType = function (type) {
    var namedType = type.kind === "NON_NULL" ? type.ofType : type;
    return namedType.kind === "SCALAR" && namedType.name === "Int";
};
/**
 * ra-data-hasura forwards filter values as they are, so a text input (or a
 * filter restored from the URL) sends an `Int` column a string, which Hasura
 * rejects. Send valid GraphQL Int values as numbers and leave anything else
 * as typed, so it is never rounded, truncated or turned into zero.
 */
function applyIntFilters(resources, resourceName, filter) {
    var _a;
    var fields = (_a = resources === null || resources === void 0 ? void 0 : resources.find(function (resource) { return resource.type.name === resourceName; })) === null || _a === void 0 ? void 0 : _a.type.fields;
    if (!fields) {
        return filter;
    }
    return Object.fromEntries(Object.entries(filter).map(function (_a) {
        var key = _a[0], value = _a[1];
        if (typeof value !== "string" || !INTEGER_REGEXP.test(value)) {
            return [key, value];
        }
        var field = fields.find(function (f) { return f.name === key; });
        var number = Number(value);
        return field &&
            isIntType(field.type) &&
            number >= GRAPHQL_INT_MIN &&
            number <= GRAPHQL_INT_MAX
            ? [key, number]
            : [key, value];
    }));
}
export var customBuildQuery = function (introspectionResults) { return function (raFetchType, resourceName, params) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11;
    var sort = params.sort;
    if (isString(resourceName) && raFetchType === "GET_LIST") {
        if (!(sort === null || sort === void 0 ? void 0 : sort.field) ||
            (COLUMNS_MAP[resourceName] && !COLUMNS_MAP[resourceName].includes(sort.field))) {
            params.sort = undefined;
        }
        var validFilters_1 = COLUMNS_MAP[resourceName];
        if (validFilters_1) {
            Object.keys(params.filter).forEach(function (f) {
                // A boolean expression the portal builds itself, such as
                // the name search's `_or`, reaches Hasura unchanged.
                if (BOOLEAN_OPERATORS.includes(f) && isRawQuery(params.filter[f]))
                    return;
                // ra-data-hasura reads `column@operator` keys, such as
                // `name@_ilike`, and comma-joins several of them; each
                // must name one of the table's columns.
                var columns = f.split(",").map(function (key) { return key.split("@")[0]; });
                if (!columns.every(function (column) { return validFilters_1.includes(column); })) {
                    console.log("removing ".concat(resourceName, ".filter.").concat(f));
                    delete params.filter[f];
                }
            });
        }
        if (params.filter) {
            params.filter = applyIntFilters(introspectionResults === null || introspectionResults === void 0 ? void 0 : introspectionResults.resources, resourceName, params.filter);
        }
    }
    if (resourceName === "electoral_log" && raFetchType === "GET_LIST") {
        var tenantLog = ((_a = params.meta) === null || _a === void 0 ? void 0 : _a[LOG_SCOPE_META]) === ELogScope.TENANT;
        var validFilters_2 = __spreadArray(__spreadArray(__spreadArray(__spreadArray([], (tenantLog ? [] : ["election_event_id"]), true), [
            "user_id",
            "username",
            "created",
            "statement_timestamp",
            "statement_kind",
            LOG_FILTER_EVENT_TYPE,
            LOG_FILTER_LOG_TYPE,
            // Messages a SendCommunications entry records (the Messaging tab's Failed links).
            LOG_FILTER_CHANNEL,
            LOG_FILTER_OUTCOME
        ], false), ELECTORAL_LOG_RANGE_FILTERS, true), [
            ELECTORAL_LOG_ZONE_FILTER,
            ELECTORAL_LOG_DEFAULT_ZONE_FILTER,
        ], false);
        Object.keys(params.filter).forEach(function (f) {
            if (!validFilters_2.includes(f)) {
                delete params.filter[f];
            }
        });
        params = __assign(__assign({}, params), { filter: electoralLogInstantFilters(params.filter) });
        var resource = {
            type: {
                fields: [],
                name: resourceName,
            },
        };
        return __assign(__assign({ query: tenantLog ? LIST_TENANT_LOG : getElectoralLog(params) }, (tenantLog ? { context: TENANT_LOG_CONTEXT } : {})), { variables: getElectoralLogVariables(buildVariables(introspectionResults)(resource, raFetchType, params, null)), parseResponse: function (res) {
                var response = res.data.listElectoralLog;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            } });
    }
    else if (resourceName === "sequent_backend_report" && raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_b = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _b === void 0 ? void 0 : _b.order_by) {
            var validOrderBy_1 = [
                "id",
                "created_at",
                "election_id",
                "report_type",
                "template_alias",
                "encryption_policy",
            ];
            ret.variables.order_by = Object.fromEntries(Object.entries(((_c = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _c === void 0 ? void 0 : _c.order_by) || {}).filter(function (_a) {
                var key = _a[0];
                return validOrderBy_1.includes(key);
            }));
            if ((_d = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _d === void 0 ? void 0 : _d.order_by) {
                ret.variables.order_by = [__assign({}, ret.variables.order_by), { id: "asc" }];
            }
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_tasks_execution" &&
        raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_e = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _e === void 0 ? void 0 : _e.order_by) {
            var validOrderBy_2 = [
                "annotations",
                "created_at",
                "election_event_id",
                "end_at",
                "executed_by_user",
                "execution_status",
                "id",
                "labels",
                "logs",
                "name",
                "start_at",
                "tenant",
                "tenant_id",
                "type",
            ];
            ret.variables.order_by = Object.fromEntries(Object.entries(((_f = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _f === void 0 ? void 0 : _f.order_by) || {}).filter(function (_a) {
                var key = _a[0];
                return validOrderBy_2.includes(key);
            }));
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_scheduled_event" &&
        raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_g = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _g === void 0 ? void 0 : _g.order_by) {
            ret.variables.order_by = [__assign({}, ret.variables.order_by), { id: "asc" }];
        }
        var electionIds = (_l = (_k = (_j = (_h = params === null || params === void 0 ? void 0 : params.filter) === null || _h === void 0 ? void 0 : _h.event_payload) === null || _j === void 0 ? void 0 : _j.value) === null || _k === void 0 ? void 0 : _k._contains) === null || _l === void 0 ? void 0 : _l.election_id;
        if (electionIds) {
            var newAnd = ret.variables.where._and.filter(function (and) { return !("event_payload" in and); });
            newAnd.push({
                _or: __spreadArray(__spreadArray([], electionIds.map(function (electionId) { return ({
                    event_payload: {
                        _contains: {
                            election_id: electionId,
                        },
                    },
                }); }), true), [
                    {
                        event_payload: {
                            _contains: {
                                election_id: null,
                            },
                        },
                    },
                ], false),
            });
            ret.variables.where._and = newAnd;
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_ballot_publication" &&
        raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_o = (_m = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _m === void 0 ? void 0 : _m.where) === null || _o === void 0 ? void 0 : _o._and) {
            if (!((_p = params === null || params === void 0 ? void 0 : params.filter) === null || _p === void 0 ? void 0 : _p.election_id)) {
                ret.variables.where._and.push({
                    election_id: { _is_null: true },
                });
            }
            else {
                var indexToReplace = ret.variables.where._and.findIndex(function (el) { return el === null || el === void 0 ? void 0 : el.election_id; });
                ret.variables.where._and[indexToReplace] = {
                    election_ids: { _contains: [(_q = params === null || params === void 0 ? void 0 : params.filter) === null || _q === void 0 ? void 0 : _q.election_id] },
                };
            }
        }
        return ret;
    }
    else if (resourceName === "user" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "user",
            },
        };
        return {
            query: LIST_USERS,
            variables: customBuildGetUsersVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_users;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "role" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "role",
            },
        };
        return {
            query: getRoles(params.filter),
            variables: buildVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_roles;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "permission" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "role",
            },
        };
        return {
            query: getPermissions(params.filter),
            variables: buildVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_permissions;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "ip_address" && raFetchType === "GET_LIST") {
        return {
            query: FORENSIC_NETWORK_SUMMARY,
            variables: {
                electionEventId: params.filter.election_event_id,
                electionId: params.filter.election_id || null,
                ip: params.filter.ip || null,
                country: params.filter.country || null,
                limit: (_s = (_r = params.pagination) === null || _r === void 0 ? void 0 : _r.perPage) !== null && _s !== void 0 ? _s : 25,
                offset: (((_u = (_t = params.pagination) === null || _t === void 0 ? void 0 : _t.page) !== null && _u !== void 0 ? _u : 1) - 1) * ((_w = (_v = params.pagination) === null || _v === void 0 ? void 0 : _v.perPage) !== null && _w !== void 0 ? _w : 25),
            },
            parseResponse: function (res) {
                var response = res.data.forensic_network_summary;
                return { data: response.payload.items, total: response.payload.total };
            },
        };
    }
    else if (resourceName === "sequent_backend_applications" && raFetchType === "GET_LIST") {
        // `q` is the queue's search box: the name, email or ID number the
        // applicant gave, anywhere in the applicant data.
        // A search that reads as a support reference also matches the
        // applications whose reference it is, resolved by the queue.
        var _12 = (_x = params.filter) !== null && _x !== void 0 ? _x : {}, search = _12.q, _13 = SUPPORT_REFERENCE_FILTER, referenceMatch = _12[_13], columnFilters = __rest(_12, ["q", typeof _13 === "symbol" ? _13 : _13 + ""]);
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, __assign(__assign({}, params), { filter: columnFilters }));
        if ((_y = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _y === void 0 ? void 0 : _y.order_by) {
            var validOrderBy_3 = [
                "id",
                "created_at",
                "updated_at",
                "applicant_id",
                "verification_type",
                "status",
            ];
            ret.variables.order_by = Object.fromEntries(Object.entries(((_z = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _z === void 0 ? void 0 : _z.order_by) || {}).filter(function (_a) {
                var key = _a[0];
                return validOrderBy_3.includes(key);
            }));
        }
        var filter_1 = params.filter;
        var transformedRawParams = __assign({}, ret === null || ret === void 0 ? void 0 : ret.variables.where);
        var transformedParams_1 = ret === null || ret === void 0 ? void 0 : ret.variables.where["_and"];
        // Transform applicant_data
        Object.keys(filter_1).forEach(function (key) {
            if (key === "applicant_data" && typeof filter_1[key] === "object") {
                var flattened_1 = flattenObject(filter_1[key]);
                Object.keys(flattened_1).forEach(function (newField) {
                    var _a;
                    transformedParams_1.push({
                        applicant_data: {
                            _contains: (_a = {}, _a[newField] = flattened_1[newField], _a),
                        },
                    });
                });
            }
        });
        if (typeof search === "string" && search.trim()) {
            var text = { applicant_data: { _cast: { String: { _ilike: "%".concat(search.trim(), "%") } } } };
            var match = referenceMatch;
            var reference = normalizeSupportReference(search);
            transformedParams_1.push(reference && (match === null || match === void 0 ? void 0 : match.reference) === reference
                ? { _or: [text, { id: { _in: match.ids } }] }
                : text);
        }
        ret.variables.where = transformedRawParams;
        return ret;
    }
    else if (resourceName === "sequent_backend_election_by_external_id" &&
        raFetchType === "GET_MANY") {
        var externalIds = ((_0 = params === null || params === void 0 ? void 0 : params.ids) !== null && _0 !== void 0 ? _0 : []).map(String);
        var electionEventId = (_6 = (_3 = (_2 = (_1 = params === null || params === void 0 ? void 0 : params.meta) === null || _1 === void 0 ? void 0 : _1.filter) === null || _2 === void 0 ? void 0 : _2.election_event_id) !== null && _3 !== void 0 ? _3 : (_5 = (_4 = params === null || params === void 0 ? void 0 : params.meta) === null || _4 === void 0 ? void 0 : _4.filter) === null || _5 === void 0 ? void 0 : _5.electionEventId) !== null && _6 !== void 0 ? _6 : null;
        return {
            query: GET_ELECTIONS_BY_EXTERNAL_ID,
            variables: {
                external_ids: externalIds,
                election_event_id: electionEventId,
            },
            parseResponse: function (res) {
                var _a, _b;
                var rows = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.sequent_backend_election) !== null && _b !== void 0 ? _b : [];
                return {
                    data: rows.map(function (r) { return (__assign(__assign({}, r), { id: r.external_id })); }),
                };
            },
        };
    }
    else if (resourceName === "sequent_backend_tally_session_execution" &&
        raFetchType === "GET_LIST" &&
        ((_7 = params === null || params === void 0 ? void 0 : params.meta) === null || _7 === void 0 ? void 0 : _7.latestPerTallySession)) {
        params.filter = __assign(__assign({}, params.filter), { distinct_on: ["tally_session_id"] });
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_8 = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _8 === void 0 ? void 0 : _8.order_by) {
            ret.variables.order_by = [
                { tally_session_id: "asc" },
                { created_at: "desc_nulls_last" },
                { id: "desc" },
            ];
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_tally_sheet" && raFetchType === "GET_LIST") {
        applyJsonbTextSearchFilter(params.filter, "labels");
        applyJsonbTextSearchFilter(params.filter, "annotations");
        applyUuidFilter(params.filter, "import_id");
        if ((_9 = params === null || params === void 0 ? void 0 : params.meta) === null || _9 === void 0 ? void 0 : _9.distinctBallotBoxes) {
            // Show only one row per ballot box (area/contest/channel)
            params.filter = __assign(__assign({}, params.filter), { distinct_on: ["area_id", "contest_id", "channel"] });
        }
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if (((_10 = params === null || params === void 0 ? void 0 : params.meta) === null || _10 === void 0 ? void 0 : _10.distinctBallotBoxes) && ((_11 = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _11 === void 0 ? void 0 : _11.order_by)) {
            ret.variables.order_by = [
                { area_id: "asc" },
                { contest_id: "asc" },
                { channel: "asc" },
                { version: "desc" },
            ];
        }
        return ret;
    }
    return buildQuery(introspectionResults)(raFetchType, resourceName, params);
}; };
function flattenObject(obj, prefix) {
    if (prefix === void 0) { prefix = ""; }
    var result = {};
    Object.keys(obj).forEach(function (key) {
        var newKey = prefix ? "".concat(prefix, ".").concat(key) : key;
        if (typeof obj[key] === "object" && obj[key] !== null && !("_ilike" in obj[key])) {
            // Recursively flatten only if it's an object and doesn't have `_ilike`
            Object.assign(result, flattenObject(obj[key], newKey));
        }
        else if ("_ilike" in obj[key]) {
            // Extract `_ilike` value
            result[newKey] = obj[key]["_ilike"];
        }
    });
    return result;
}
